import { ConfigurationImpl as Configuration } from '@/api/Configuration';
import { toApiStoreCreateRequest, toApiStoreUpdateRequest, toStore } from '@/api/mappers/mapStore';
import {
  StoresApi as ApiStoresApi,
  GetStoreMetricsRequest,
  ListStoresRequest,
} from '@/gen/api/apis/StoresApi';
import {
  Store,
  StoreAssociation,
  StoreCreate,
  StoreMetrics,
  Stores,
  StoreUpdate,
} from '@/models/Store';
import { toNonEmptyArray } from '@/utils/array';

export interface StoreFilters {
  retailerId: string | string[];
  supplierId: string | string[];
}

interface StoresApi {
  listStores(offset?: number, limit?: number, include?: StoreAssociation[]): Promise<Stores>;
  findStores(
    query?: string,
    filters?: Partial<StoreFilters>,
    offset?: number,
    limit?: number,
    include?: StoreAssociation[],
  ): Promise<Stores>;
  getStore(id: string, include?: StoreAssociation[]): Promise<Store | undefined>;
  getStoreWithSupplierContext(
    id: string,
    supplierId: string,
    include?: StoreAssociation[],
  ): Promise<Store | undefined>;
  createStore(store: StoreCreate): Promise<void>;
  updateStore(id: string, store: StoreUpdate): Promise<void>;
  deleteStore(id: string): Promise<void>;
  getStoreMetrics(filters?: Partial<StoreFilters>): Promise<StoreMetrics>;
}

export class StoresApiImpl implements StoresApi {
  private storesApi: ApiStoresApi;

  constructor(configuration: Configuration) {
    this.storesApi = new ApiStoresApi(configuration);
  }

  async listStores(
    offset: number = 0,
    limit: number = 10,
    include?: StoreAssociation[],
  ): Promise<Stores> {
    try {
      const params: ListStoresRequest = {
        offset,
        limit,
        include,
      };

      const response = await this.storesApi.listStores(params);
      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toStore),
      };
    } catch (error) {
      console.error('Error listing stores:', error);
      throw error;
    }
  }

  async findStores(
    query?: string,
    filters?: Partial<StoreFilters>,
    offset?: number,
    limit?: number,
    include?: StoreAssociation[],
  ): Promise<Stores> {
    try {
      const params: ListStoresRequest = {
        query,
        offset,
        limit,
        include,
        retailerId: toNonEmptyArray(filters?.retailerId),
        supplierId: toNonEmptyArray(filters?.supplierId),
      };

      const response = await this.storesApi.listStores(params);
      return {
        totalCount: response.totalCount,
        estimatedTotalCount: response.estimatedTotalCount,
        items: response.items.map(toStore),
      };
    } catch (error) {
      console.error('Error finding stores:', error);
      throw error;
    }
  }

  async getStore(id: string, include?: StoreAssociation[]): Promise<Store | undefined> {
    try {
      const response = await this.storesApi.getStore({
        id,
        include,
      });
      return toStore(response);
    } catch (error) {
      console.error('Error getting store:', error);
      throw error;
    }
  }

  async getStoreWithSupplierContext(
    id: string,
    supplierId: string,
    include?: StoreAssociation[],
  ): Promise<Store | undefined> {
    try {
      const response = await this.storesApi.getStore({
        id,
        include,
        supplierId,
      });
      return toStore(response);
    } catch (error) {
      console.error('Error getting store:', error);
      throw error;
    }
  }

  async createStore(store: StoreCreate): Promise<void> {
    try {
      await this.storesApi.createStore({
        storeCreateRequest: toApiStoreCreateRequest(store),
      });
    } catch (error) {
      console.error('Error creating store:', error);
      throw error;
    }
  }

  async updateStore(id: string, store: StoreUpdate): Promise<void> {
    try {
      await this.storesApi.updateStore({
        id,
        storeUpdateRequest: toApiStoreUpdateRequest(store),
      });
    } catch (error) {
      console.error('Error updating store:', error);
      throw error;
    }
  }

  async deleteStore(id: string): Promise<void> {
    try {
      await this.storesApi.deleteStore({ id });
    } catch (error) {
      console.error('Error deleting store:', error);
      throw error;
    }
  }

  async getStoreMetrics(filters?: Partial<StoreFilters>): Promise<StoreMetrics> {
    try {
      const params: GetStoreMetricsRequest = {
        retailerId: toNonEmptyArray(filters?.retailerId),
        supplierId: toNonEmptyArray(filters?.supplierId),
      };

      const response = await this.storesApi.getStoreMetrics(params);

      return {
        storeCount: response.storeCount,
        activeStoreCount: response.activeStoreCount,
      };
    } catch (error) {
      console.error('Error getting store metrics:', error);
      throw error;
    }
  }
}

export default StoresApi;
